import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import { ContactAnalysisColumns } from "@/shared/utils/utils";
import {
  getEngagementStatusVariant,
  getTextInCapitalize,
  getVariant,
} from "@/shared/utils/helperFunctions";
import ProspectProfileRow from "@/shared/components/ui/Avatar/ProspectProfileRow";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { useFetchCampaignLeads } from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import Loader from "@/shared/components/ui/Loaders/Loader";
const PAGE_SIZE = 10;

const getLeadId = (item) => item?.id ?? item?.contact_id ?? item?.email;

const CampaignContactTable = () => {
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState([]);
  const loadingMoreRef = useRef(false);
  const prevCampaignIdRef = useRef(null);

  const campaignId = useCampaignId();
  const {
    data: campaignLeads,
    isLoading: isLoadingCampaignLeads,
    isFetching,
  } = useFetchCampaignLeads({
    campaignId,
    page,
    limit: PAGE_SIZE,
    searchQuery: "",
  });

  const totalCount = campaignLeads?.total ?? 0;

  // Reset when campaign changes
  useEffect(() => {
    if (prevCampaignIdRef.current !== campaignId) {
      prevCampaignIdRef.current = campaignId;
      setPage(1);
      setAccumulatedData([]);
    }
  }, [campaignId]);

  // Sync accumulated data with campaign leads
  useEffect(() => {
    if (!campaignLeads) return;

    const leads = campaignLeads.leads ?? [];
    const currentPage = campaignLeads.page ?? 1;

    if (currentPage === 1) {
      setAccumulatedData(leads);
    } else if (leads.length > 0) {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map(getLeadId));
        const newItems = leads.filter(
          (item) => !existingIds.has(getLeadId(item)),
        );
        return [...prev, ...newItems];
      });
    }
  }, [campaignLeads]);

  // Reset loading ref when fetching  completes
  useEffect(() => {
    if (!isFetching) {
      loadingMoreRef.current = false;
    }
  }, [isFetching]);

  const loadMoreData = useCallback(() => {
    if (loadingMoreRef.current || isFetching) return;
    if (accumulatedData.length >= totalCount) return;
    if (totalCount === 0) return;

    loadingMoreRef.current = true;
    setPage((prev) => prev + 1);
  }, [accumulatedData.length, totalCount, isFetching]);

  const renderCell = useCallback((item, columnKey) => {
    switch (columnKey) {
      case "name":
        return (
          <div className="text-wrap">
            <ProspectProfileRow
              avatarUrl={item?.avatarUrl}
              name={item?.full_name}
              email={item?.email}
              number={item?.matchScore}
            />
          </div>
        );

      case "company":
        return (
          <div className="text-xs text-gray-500 text-wrap break-all break-words">
            {item.company}
          </div>
        );

      case "matchScore":
        return (
          <StatusChip
            label={`${item?.matchScore ? item?.matchScore + "%" : "N/A"}`}
            variant={getVariant(item?.matchScore)}
          />
        );

      case "engagement_status":
        return (
          <StatusChip
            label={`${getTextInCapitalize(item?.engagementStatus?.status) || "N/A"}`}
            variant={getEngagementStatusVariant(item?.engagementStatus?.status)}
            tooltipContent={`${item?.engagementStatus?.description || "N/A"}`}
            tooltipPlacement="left"
          />
        );

      case "engagementScore":
        return (
          <StatusChip
            label={`${getTextInCapitalize(item?.engagementStatus?.status) || "Cold"} · ${item?.runtimeEngagementScore ?? 0}`}
            variant={getEngagementStatusVariant(item?.engagementStatus?.status)}
          />
        );

      default:
        return item[columnKey];
    }
  }, []);

  if (isLoadingCampaignLeads && accumulatedData.length === 0) {
    return (
      <div className="w-full h-[calc(100dvh-450px)] flex items-center justify-center">
        <Loader text="Loading contacts..." />
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-0">
      {!isFetching &&
        !isLoadingCampaignLeads &&
        accumulatedData.length === 0 ? (
        <div className="flex flex-col gap-1 items-center justify-center h-full w-full">
          <h3 className="text-lg font-bold text-gray-900">No contacts found</h3>
          <p className="text-[0.85rem] text-gray-500 2xl:max-w-[600px] xl:max-w-[550px] lg:max-w-[450px] md:max-w-[300px] max-w-[150px] text-center">
            No contacts found for this campaign add leads to this campaign to
            see the contacts here
          </p>
        </div>
      ) : (
        <CustomTable
          columns={ContactAnalysisColumns}
          renderCell={renderCell}
          data={accumulatedData}
          totalCount={totalCount}
          onScrollEnd={loadMoreData}
          rowsPerPage={PAGE_SIZE}
          height="100%"
          tableLayout="auto"
          showCellBorders
          pinFirstColumn
        />
      )}

      {isFetching && accumulatedData.length > 0 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 bg-white/95 shadow rounded-full text-xs text-gray-600">
          <span className="animate-pulse">Loading more...</span>
        </div>
      )}
    </div>
  );
};

export default CampaignContactTable;
